/**
 * NamedDefaultDropFormBody — drop-named-default form body. mssql-only.
 * The standalone toggle picks between `ALTER TABLE … DROP CONSTRAINT name`
 * (default — drops an inline `ADD CONSTRAINT df_name DEFAULT (…) FOR col`
 * binding) and `DROP DEFAULT name` (drops a free-floating object created
 * via the deprecated `CREATE DEFAULT` form).
 *
 * Engine gating: caller (`NamedDefaultDropPopover`) renders the trigger
 * disabled on non-mssql engines with a tooltip pointing at the column
 * form. Included as a defensive runtime guard.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B5.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import {
  commitDropNamedDefault,
  validateDropNamedDefaultForEngine,
} from '../../edit/edits/dropNamedDefault';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

type DropMode = 'inline' | 'standalone';

export interface NamedDefaultDropFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function NamedDefaultDropFormBody(props: NamedDefaultDropFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [mode, setMode] = useState<DropMode>('inline');
  const [constraintName, setConstraintName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    setError(null);
    const args = {
      constraintName: constraintName.trim(),
      standalone: mode === 'standalone' ? true : undefined,
    };
    const validationError = validateDropNamedDefaultForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitDropNamedDefault(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Drop named DEFAULT</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1" data-testid={`${testId}-form-mode`}>
        <Label className="text-xs">Form</Label>
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as DropMode)}
          className="flex gap-3 text-xs"
        >
          <label className="flex items-center gap-1">
            <RadioGroupItem value="inline" data-testid={`${testId}-mode-inline`} />
            <span>Inline (DROP CONSTRAINT)</span>
          </label>
          <label className="flex items-center gap-1">
            <RadioGroupItem value="standalone" data-testid={`${testId}-mode-standalone`} />
            <span>Standalone (DROP DEFAULT)</span>
          </label>
        </RadioGroup>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Constraint name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={constraintName}
          onChange={(e) => setConstraintName(e.target.value)}
          placeholder="df_table_status"
          className="h-8 font-mono text-xs"
        />
      </div>

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="button" size="sm" variant="destructive" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Dropping…' : 'Drop'}
        </Button>
      </div>
    </div>
  );
}
